import type { Editor } from "@tiptap/react"

export const MAX_FILE_SIZE = 5 * 1024 * 1024
export const MAX_FILE_UPLOAD_SIZE = 10 * 1024 * 1024

export function isValidPosition(pos: number | undefined): pos is number {
  return typeof pos === "number" && !isNaN(pos) && pos >= 0
}

export function focusNextNode(editor: Editor) {
  const { state } = editor
  const { selection } = state
  const { $from } = selection
  
  const nextPos = $from.pos + 1
  if (nextPos <= state.doc.content.size) {
    editor.commands.focus(nextPos)
  }
}

export function isNodeInSchema(editor: Editor | null, nodeName: string): boolean {
  if (!editor) return false
  return nodeName in editor.schema.nodes
}

export function isExtensionAvailable(editor: Editor | null, extensionName: string): boolean {
  if (!editor) return false
  return editor.extensionManager.extensions.some(
    (ext) => ext.name === extensionName
  )
}

export const handleImageUpload = async (
  file: File,
  onProgress?: (event: { progress: number }) => void,
  abortSignal?: AbortSignal
): Promise<string> => {
  if (!file) {
    throw new Error("Файл не выбран")
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(
      `Размер файла превышает максимально допустимый (${MAX_FILE_SIZE / (1024 * 1024)}МБ)`
    )
  }

  return new Promise((resolve, reject) => {
    if (abortSignal?.aborted) {
      reject(new Error("Загрузка отменена"))
      return
    }

    const reader = new FileReader()
    
    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100)
        onProgress?.({ progress })
      }
    }

    reader.onload = () => {
      onProgress?.({ progress: 100 })
      resolve(reader.result as string)
    }

    reader.onerror = () => {
      reject(new Error("Не удалось прочитать файл"))
    }

    abortSignal?.addEventListener("abort", () => {
      reader.abort()
      reject(new Error("Загрузка отменена"))
    })

    reader.readAsDataURL(file)
  })
}

export const handleFileUpload = async (
  file: File,
  onProgress?: (event: { progress: number }) => void,
  abortSignal?: AbortSignal
): Promise<string> => {
  if (!file) {
    throw new Error("Файл не выбран")
  }

  if (file.size > MAX_FILE_UPLOAD_SIZE) {
    throw new Error(
      `Размер файла превышает максимально допустимый (${MAX_FILE_UPLOAD_SIZE / (1024 * 1024)}МБ)`
    )
  }

  return new Promise((resolve, reject) => {
    if (abortSignal?.aborted) {
      reject(new Error("Загрузка отменена"))
      return
    }

    const reader = new FileReader()
    
    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100)
        onProgress?.({ progress })
      }
    }

    reader.onload = () => {
      onProgress?.({ progress: 100 })
      resolve(reader.result as string)
    }

    reader.onerror = () => {
      reject(new Error("Не удалось прочитать файл"))
    }

    abortSignal?.addEventListener("abort", () => {
      reader.abort()
      reject(new Error("Загрузка отменена"))
    })

    reader.readAsDataURL(file)
  })
}
